import useInvokeAsync from 'components/App/hooks/useInvokeAsync'
import useTheme, { Theme } from 'components/App/hooks/useTheme'
import { getPoiMarkerId, MarkerState, MarkerType, Theme as PoiMarkerTheme } from 'lib/cartography'
import React, { useCallback } from 'react'

import Marker from '../Marker'
import SvgMarkerIcon from '../SvgMarkerIcon'

interface PdsMarkerProps {
  id: string
  type?: MarkerType
  lat: number
  lng: number
  zIndex: number
  isHovered?: boolean
  categoryId?: string
  isStared?: boolean
  cursor?: string
  onMarkerCreate: (marker: H.map.Marker) => void
  onMouseLeave?: (id: string) => void
  onMouseEnter?: (id: string) => void
  onClick?: (id: string) => void
}

function PdsMarker({
  id,
  type = MarkerType.poi,
  lat,
  lng,
  zIndex,
  categoryId,
  isHovered,
  cursor,
  isStared = false,
  onMarkerCreate,
  onMouseEnter,
  onMouseLeave,
  onClick,
}: PdsMarkerProps): JSX.Element | null {
  const [theme, _] = useTheme()
  const markerTheme = toPoiMarkerTheme(theme)
  const { result } = useInvokeAsync(
    useCallback(async () => {
      const iconState =
        isHovered !== undefined && isHovered
          ? MarkerState.hoverSpotlight
          : MarkerState.defaultSpotlight
      const result = await getPoiMarkerId(categoryId, type, markerTheme, iconState)
      return result
    }, [categoryId, isHovered, markerTheme, type])
  )
  const spriteId = result as string | undefined
  const [width, height] =
    spriteId !== undefined ? getMarkerSize(spriteId, isHovered, isStared) : [32, 42]
  const markerStyle =
    width !== undefined && height !== undefined ? getMarkerStyle(width, height) : {}
  const markerId = spriteId !== undefined ? id : ''

  const handleOnMouseEnter = useCallback(
    (id: string) => {
      onMouseEnter?.(id)
    },
    [onMouseEnter]
  )

  const handleOnMouseLeave = useCallback(
    (id: string) => {
      onMouseLeave?.(id)
    },
    [onMouseLeave]
  )

  const handleOnClick = useCallback(() => {
    if (onClick !== undefined) {
      onClick(id)
    }
  }, [id, onClick])

  return spriteId !== undefined ? (
    <Marker
      id={markerId}
      width={width}
      height={height}
      position={{
        lat,
        lng,
      }}
      zIndex={zIndex}
      onMarkerCreate={onMarkerCreate}
      onMouseEnter={handleOnMouseEnter}
      onMouseLeave={handleOnMouseLeave}
      onClick={handleOnClick}
    >
      <SvgMarkerIcon
        width={String(width)}
        height={String(height)}
        style={markerStyle}
        isStared={isStared}
        cursor={cursor}
      >
        <use href={`#${spriteId}`} />
      </SvgMarkerIcon>
    </Marker>
  ) : null
}

function getMarkerSize(
  svgId: string,
  hasHoverState?: boolean,
  hasStarState?: boolean
): [number, number] {
  const svgSize =
    svgId !== undefined
      ? document
          .getElementById(svgId)
          ?.getAttribute('viewBox')
          ?.split(' ')
          .filter((r) => Number(r))
      : undefined
  const finalHeight = svgSize !== undefined ? Number(svgSize[1]) : 32
  const finalWidth =
    svgSize !== undefined
      ? hasStarState !== undefined && hasStarState
        ? hasHoverState !== undefined && hasHoverState
          ? Number(svgSize[0]) + 8
          : Number(svgSize[0]) + 14
        : Number(svgSize[0])
      : 42

  return [finalWidth, finalHeight]
}

function getMarkerStyle(width: number, height: number): React.CSSProperties {
  return {
    left: `-${width / 2}px`,
    top: `-${height}px`,
  }
}

function toPoiMarkerTheme(theme: Theme): PoiMarkerTheme {
  switch (theme) {
    case Theme.dark:
      return PoiMarkerTheme.dark
    case Theme.light:
      return PoiMarkerTheme.light
    case Theme.hybrid:
      return PoiMarkerTheme.hybrid
  }
}

export default React.memo(PdsMarker)
